import { BaseHit, Hit } from 'instantsearch.js';
import { getLocalizationInfo } from '@/project.config';
import { localeMapping } from '@/helpers/constants/localeMapping';

const setMasterVariantToChromeVariant = (productHit: Hit<any>) => {
  if (productHit) {
    const chromeVariant = productHit?.variants?.filter((variant:any) => variant.attributes?.colour?.toLowerCase() === 'chrome')?.[0];
    const stainlessSteelVariant = productHit?.variants?.filter((variant:any) => variant.attributes?.colour?.toLowerCase() === 'stainless steel')?.[0];
    const stainlessVariant = productHit?.variants?.filter((variant:any) => variant.attributes?.colour?.toLowerCase() === 'stainless')?.[0];
    const brushedNickelVariant = productHit?.variants?.filter((variant:any) => variant.attributes?.colour?.toLowerCase() === 'brushed nickel')?.[0];
    const potentialMasterVariant = chromeVariant || stainlessSteelVariant || stainlessVariant || brushedNickelVariant;

    if (potentialMasterVariant)
      productHit.masterVariantID = potentialMasterVariant.id;
  }
}

export const mapProduct = (hit: Hit<BaseHit>, locale: string, searchQuery?: string): any => {
  const productHit = hit as Hit<any>;
  const localeKey = localeMapping[locale];
  const extractPriceAlgolia = (prices: any, locale: any) => {
    const price = prices[locale.currency];
    if (price) {
      const minPrice = price.min;
      return {
        centAmount: minPrice,
        currencyCode: locale.currency,
        fractionDigits: 2,
      };
    }
    return undefined;
  }
  const price = extractPriceAlgolia(productHit.prices || [], getLocalizationInfo(locale));

  const variants = [{
    price,
    sku: productHit.sku,
    images: productHit.images,
    attributes: productHit.attributes,
  }];
  const itemCode = variants[0]?.attributes?.['item-code'];

  const { currency, locale: currentLocale } = getLocalizationInfo(locale);
  const slug = (productHit.slug?.[currentLocale.replace('_', '-')] ?? productHit.slug?.[`${localeKey}`])
    ?.toLowerCase()
    ?.replace(/\s/g, '-')
    ?.replace('/', '');

  setMasterVariantToChromeVariant(productHit);
  const masterVariant = productHit?.variants?.filter((variant:any) => variant.id === productHit?.masterVariantID)?.[0];
  const findSkuByItemCode = (itemCode: string) => {
    if (itemCode) {
      const matchingVariant = findSkuByItemCodeExactMatch(itemCode)
        ||
        productHit?.variants?.find((variant: any) =>
          variant?.attributes?.['item-code']?.toLowerCase().includes(itemCode.toLowerCase())
        );
      return matchingVariant;
    }
    else
      return null;
  };

  const findSkuByItemCodeExactMatch = (itemCode: string) => {
    const matchingVariant = productHit?.variants?.find((variant: any) =>
      variant?.attributes?.['item-code']?.toLowerCase() == itemCode.toLowerCase()
    );
    return matchingVariant;
  };

  const matchedSku = findSkuByItemCode(searchQuery ?? '') ?? masterVariant ?? productHit?.variants?.[0];

  const generateUrl = () => {
    if (variants !== undefined) {
      // If the item is a product, we want to generate a url for a matching variant or the first variant if no matching variant is found
      const variant = variants.find((variant: any) => variant.isMatchingVariant !== false) ?? variants[0];
      return `/${slug}/p/${variant.sku}`;
    }
    return `/${slug}/p/${productHit?.sku}`;
  }

  const exclusiveProduct: any = {
    'en-AU': variants[0]?.attributes?.['exclusive-product-AU'],
    'en-NZ': variants[0]?.attributes?.['exclusive-product-NZ'],
  };
  const isNew = variants[0]?.attributes?.['IsNew'];
  const categories = (productHit?.categories && (productHit?.categories as any)[localeKey]?.lvl1) || [];
  const [mainCategory, subCategory] = categories.length ? categories[0].split(' > ') : [null, null];
  const runDown = variants[0]?.attributes?.['item-attributes']?.find((attr: { name: string }) => attr.name === 'lifecycle')
    ?.value[localeKey]?.toLowerCase() === 'run down';
  const lifecycle = variants[0]?.attributes?.['item-attributes']?.find((attr: { name: string }) => attr.name === 'lifecycle')
    ?.value[localeKey];
  return {
    productId: productHit?.productId,
    productcode: matchedSku?.attributes?.['item-code'],
    name: productHit?.name?.[currentLocale.replace('_', '-')],
    variants,
    url: generateUrl(),
    exclusiveProduct: Array.isArray(exclusiveProduct[localeKey]) && exclusiveProduct[localeKey].length > 0 && exclusiveProduct[localeKey][0]?.toLowerCase() !== 'null',
    isNew,
    image: variants[0]?.images?.[0] ? [variants[0].images[0]] : undefined,
    images: variants[0].images,
    models: itemCode,
    price: (variants[0].price?.centAmount ?? 0) / Math.pow(10, variants[0].price?.fractionDigits ?? 2),
    category: subCategory ?? mainCategory,
    currency: (variants[0].price?.currencyCode ?? 'AUD'),
    isBuilderRange:(variants[0]?.attributes?.['builder-range']?.[localeKey]?.toLowerCase() === 'yes'),
    runDown,
    lifecycle
  };
};

export const mapFrontasticProductToHits = (productHit: any, locale: string, searchQuery?: string): any => {
  const masterVariant = productHit?.variants?.[0]
  return {
    ...productHit,
    productId: productHit?.productId,
    selectedVariant: masterVariant,
    productcode: masterVariant?.attributes?.['product-code']
  };
};
